import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, BookOpen, Heart, Star, Sparkles, Send, CheckCircle, Users, PenTool, Palette, Book, ShoppingBag, Globe, Gift } from "lucide-react";
import axios from "axios";
import { ShareSection } from "../components/ShareSection";
import TestimonialsStrip from "../components/TestimonialsStrip";

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [interestedInArc, setInterestedInArc] = useState(false);
  const [signupStatus, setSignupStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/signup`, {
        email,
        name,
        interested_in_arc: interestedInArc,
      });
      setSignupStatus(response.data);
      if (!response.data.already_subscribed) {
        setEmail("");
        setName("");
        setInterestedInArc(false);
      }
    } catch (error) {
      setSignupStatus({ message: "Oops! Something went wrong. Please try again.", error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero - Coming Soon */}
      <section className="py-16 md:py-24 px-4 md:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#2A9D8F] text-white px-4 py-2 rounded-full font-bold text-sm border-4 border-[#1D3557] mb-6">
              <Sparkles className="w-4 h-4" />
              Now Available!
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#1D3557] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Detective Diaries of Marchfield Meadows
            </h1>
            <p className="text-lg sm:text-xl text-[#1D3557] mb-4 leading-relaxed font-medium">
              A heartwarming children's book series where we follow the adventures of Callie the Case-Solving Cow!
            </p>
            <p className="text-base text-[#1D3557]/80 mb-8 font-medium">
              Join Callie and her farm friends as they solve mysteries around Marchfield Meadows.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#signup"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="join-waitlist-btn"
              >
                <Mail className="w-5 h-5" />
                Join the Early Readers List
              </a>
              <a
                href="#meet-callie"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="meet-callie-btn"
              >
                <Heart className="w-5 h-5" />
                Meet Callie
              </a>
            </div>
          </motion.div>

          {/* Right - Book Cover */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="book-float tactile-card p-4 max-w-md">
              <img
                src="https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/d0h3ch73_image.png"
                alt="The Detective Diaries of Marchfield Meadows Book Cover"
                className="w-full h-auto rounded-2xl"
                data-testid="book-cover-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Availability Banner */}
      <section className="py-8 px-4 md:px-8 bg-[#2A9D8F]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-2"
          >
            <p className="text-lg md:text-xl text-white font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              📚 Now live on Amazon worldwide — order yours today!
            </p>
            <p className="text-lg md:text-xl text-white font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🌍 Search ISBN <span className="text-[#E9C46A]">9781067688004</span> at your favourite bookstore worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* Choose Your Region - Purchase Section */}
      <section className="py-16 px-4 md:px-8 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Choose Your Region
            </h2>
            <p className="text-lg text-[#1D3557] font-medium">
              Get your copy of The Detective Diaries of Marchfield Meadows!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Amazon Worldwide */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="tactile-card p-8 text-center"
              data-testid="region-amazon"
            >
              <div className="text-5xl mb-4">🛒</div>
              <h3 className="text-2xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Amazon Worldwide
              </h3>
              <p className="text-[#1D3557] font-medium mb-4">
                Available on all Amazon marketplaces worldwide — US, UK, Canada, Australia, Caribbean, Latin America & more!
              </p>
              <a
                href="https://www.amazon.com/Detective-Diaries-Marchfield-Meadows/dp/1067688005"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="buy-amazon-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                Buy on Amazon
              </a>
            </motion.div>

            {/* Libraries & Bookstores */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="tactile-card p-8 text-center"
              data-testid="region-international"
            >
              <div className="text-5xl mb-4">📚</div>
              <h3 className="text-2xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Bookstores & Libraries
              </h3>
              <p className="text-[#1D3557] font-medium mb-4">
                Distributed worldwide through IngramSpark — ask any retailer or library to order it!
              </p>

              <div className="bg-[#1D3557]/5 rounded-xl p-3 mb-4 text-left">
                <p className="text-xs text-[#1D3557]/70 font-bold uppercase tracking-wide mb-1">Search by ISBN</p>
                <p className="font-mono font-bold text-[#1D3557] text-sm break-all" data-testid="isbn-display">9781067688004</p>
                <p className="text-xs text-[#1D3557]/70 font-medium mt-2">
                  Title: <span className="font-bold text-[#1D3557]">The Detective Diaries of Marchfield Meadows</span>
                </p>
                <p className="text-xs text-[#1D3557]/70 font-medium">
                  Author: <span className="font-bold text-[#1D3557]">Terata B.</span>
                </p>
              </div>

              <Link
                to="/where-to-buy"
                className="btn-teal w-full inline-flex items-center justify-center gap-2"
                data-testid="find-retailer-btn"
              >
                <Globe className="w-5 h-5" />
                Find a Retailer
              </Link>
            </motion.div>

            {/* Barbados Orders */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="tactile-card p-8 text-center border-4 border-[#E9C46A]"
              data-testid="region-barbados"
            >
              <div className="text-5xl mb-4">🇧🇧</div>
              <h3 className="text-2xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Barbados Orders
              </h3>
              <p className="text-[#1D3557] font-medium mb-6">
                Local stock has arrived! Pickup, delivery &amp; rural delivery available. Pay via PayPal, cash, BimPay, or FirstPay.
              </p>
              <Link
                to="/order/barbados"
                className="btn-secondary w-full inline-flex items-center justify-center gap-2"
                data-testid="order-barbados-btn"
              >
                <Gift className="w-5 h-5" />
                Order in Barbados
              </Link>
            </motion.div>
          </div>

          {/* Launch Date */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <p className="text-[#1D3557]/70 font-medium text-lg">
              ✨ Launched <span className="font-bold text-[#E63946]">June 1</span> — World Milk Day! 🥛
            </p>
          </motion.div>

          {/* Spread the word */}
          <div className="max-w-4xl mx-auto mt-12">
            <ShareSection variant="full" />
          </div>
        </div>
      </section>

      {/* Reader Testimonials */}
      <TestimonialsStrip />

      {/* Meet Callie Section */}
      <section id="meet-callie" className="py-16 px-4 md:px-8 bg-[#2A9D8F]/10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Meet Callie!
            </h2>
            <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
              The curious calf who leads the detective adventures
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="tactile-card p-6 max-w-sm mx-auto lg:mx-0 bg-[#F5E6A3]"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/b3r5vxf9_image.png"
                alt="Callie the Detective Calf"
                className="w-full h-auto rounded-2xl"
                data-testid="callie-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                <div className="tactile-card p-6">
                  <h3 className="text-xl font-bold text-[#1D3557] mb-3 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    <Heart className="w-5 h-5 text-[#E63946]" />
                    Curious & Kind
                  </h3>
                  <p className="text-[#1D3557] font-medium">
                    Callie is always asking questions and looking for clues. Her gentle nature helps her make friends with everyone at Marchfield Meadows.
                  </p>
                </div>
                <div className="tactile-card p-6">
                  <h3 className="text-xl font-bold text-[#1D3557] mb-3 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    <Star className="w-5 h-5 text-[#E9C46A]" />
                    Natural Detective
                  </h3>
                  <p className="text-[#1D3557] font-medium">
                    With her sharp eyes and keen sense of smell, Callie notices things others miss. She gathers clues from her farmyard friends to solve every mystery!
                  </p>
                </div>
                <div className="tactile-card p-6">
                  <h3 className="text-xl font-bold text-[#1D3557] mb-3 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    <Users className="w-5 h-5 text-[#2A9D8F]" />
                    Teamwork
                  </h3>
                  <p className="text-[#1D3557] font-medium">
                    Working together is very important for farm life, and Callie shows this throughout every adventure. Her farmyard friends help her piece together the clues to crack each case!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              About the Book
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="tactile-card p-8 text-center"
              data-testid="book-feature-1"
            >
              <div className="w-16 h-16 bg-[#E9C46A] rounded-2xl flex items-center justify-center mx-auto mb-6 border-4 border-[#1D3557]">
                <BookOpen className="w-8 h-8 text-[#1D3557]" />
              </div>
              <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Perfect for Ages 0-10
              </h3>
              <p className="text-[#1D3557] font-medium">
                Engaging stories designed for young minds, from babies enjoying the pictures to older children following Callie's adventures.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="tactile-card p-8 text-center"
              data-testid="book-feature-2"
            >
              <div className="w-16 h-16 bg-[#457B9D] rounded-2xl flex items-center justify-center mx-auto mb-6 border-4 border-[#1D3557]">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Brain Development
              </h3>
              <p className="text-[#1D3557] font-medium">
                Storytelling and mystery-solving elements that encourage critical thinking and support the developing brain.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="tactile-card p-8 text-center"
              data-testid="book-feature-3"
            >
              <div className="w-16 h-16 bg-[#2A9D8F] rounded-2xl flex items-center justify-center mx-auto mb-6 border-4 border-[#1D3557]">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Visual Stimulation
              </h3>
              <p className="text-[#1D3557] font-medium">
                Colorful, vibrant illustrations that captivate young eyes and support early visual development.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 tactile-card p-8 md:p-12 bg-[#FDFBF7]"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                The Story
              </h3>
              <p className="text-[#1D3557] font-medium leading-relaxed text-lg">
                When mysterious things start happening at Marchfield Meadows, it's up to Callie to investigate! From missing milk to strange noises in the barn, every day brings a new adventure. By gathering clues from her farmyard friends, Callie pieces together each puzzle. With wit, curiosity, and a little help from her animal pals, she discovers that every mystery has a solution—and usually a lesson about friendship along the way.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About the Author */}
      <section className="py-16 px-4 md:px-8 bg-[#E9C46A]/20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="tactile-card p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-32 h-32 bg-[#2A9D8F] rounded-full flex items-center justify-center border-4 border-[#1D3557] flex-shrink-0">
                  <PenTool className="w-12 h-12 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-[#1D3557] mb-4 text-center md:text-left" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    About the Author
                  </h2>
                  <p className="text-[#1D3557] font-medium leading-relaxed mb-4">
                    The creator of FarmSmart Kids is a livestock technician by profession with over 10 years experience in the field, 8 years experience as a livestock farmer, and over 15 years in the veterinary sector overall.
                  </p>
                  <p className="text-[#1D3557] font-medium leading-relaxed mb-4">
                    "I wanted to create stories that would spark curiosity in children while teaching them about farm animals and their importance, along with the values of hard work, friendship, and problem-solving."
                  </p>
                  <p className="text-[#1D3557] font-medium leading-relaxed">
                    "Callie and her friends are inspired by the real animals I have worked with along the way and have owned—each with their own unique personalities!"
                  </p>
                  <p className="text-[#1D3557]/70 font-medium mt-4 italic">
                    — The Author
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet the Illustrator */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="tactile-card p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-32 h-32 bg-[#E63946] rounded-full flex items-center justify-center border-4 border-[#1D3557] flex-shrink-0">
                  <Palette className="w-12 h-12 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-[#1D3557] mb-4 text-center md:text-left" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    Meet the Illustrator
                  </h2>
                  <p className="text-[#1D3557] font-medium leading-relaxed mb-4">
                    "Hey, I'm Kemar. I help aspiring brands communicate visually in the hearts of their customers. My mission is to align your visual identity with the values your brand represents."
                  </p>
                  <p className="text-[#1D3557] font-medium leading-relaxed mb-4">
                    "Designing is not just a job for me. It's a passion for seeing business owners I've worked with feel amazing about their brand's visual identity."
                  </p>
                  <p className="text-[#1D3557] font-medium leading-relaxed">
                    "I love working with small business owners who are eager to put the pieces of their identity together. Eventually these pieces of the puzzle begin to fall into place and it no longer seems as daunting or complicated. That is what gets me excited about what I do."
                  </p>
                  <p className="text-[#1D3557]/70 font-medium mt-4 italic">
                    — Kemar, Illustrator
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Activity Book Section */}
      <section className="py-16 px-4 md:px-8 bg-[#457B9D]/10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-[#2A9D8F] text-white px-4 py-2 rounded-full font-bold text-sm border-4 border-[#1D3557] mb-6">
              <Book className="w-4 h-4" />
              Also Coming Soon!
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Activity Book
            </h2>
            <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
              Extend the fun with The Detective Diaries of Marchfield Meadows Activity Book!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="tactile-card p-6 max-w-md mx-auto"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/7cmear17_image.png"
                alt="The Detective Diaries Activity Book Cover"
                className="w-full h-auto rounded-2xl"
                data-testid="activity-book-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="tactile-card p-6">
                <h3 className="text-xl font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  What's Inside?
                </h3>
                <ul className="space-y-2 text-[#1D3557] font-medium">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#E63946] rounded-full"></span>
                    Coloring pages featuring Callie and friends
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#E9C46A] rounded-full"></span>
                    Puzzles and mazes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#2A9D8F] rounded-full"></span>
                    Mystery-solving activities
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#457B9D] rounded-full"></span>
                    Farm animal facts and fun
                  </li>
                </ul>
              </div>
              <p className="text-[#1D3557] font-medium text-center">
                Coming soon — companion activity book to the main story!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Email Signup Section */}
      <section id="signup" className="py-16 px-4 md:px-8 bg-[#1D3557]">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Join the Early Readers List
            </h2>
            <p className="text-white/80 font-medium text-lg">
              Be the first to know when the book launches! Sign up to receive updates, sneak peeks, and exclusive content.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {signupStatus && !signupStatus.error ? (
              <div className="bg-[#2A9D8F] rounded-3xl p-8 text-center border-4 border-white">
                <CheckCircle className="w-16 h-16 text-white mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {signupStatus.already_subscribed ? "You're Already Signed Up!" : "You're on the List!"}
                </h3>
                <p className="text-white/90 font-medium">
                  {signupStatus.message}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSignup} className="bg-white rounded-3xl p-8 border-4 border-[#E9C46A]">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      Your Name (optional)
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="input-field"
                      placeholder="Enter your name"
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[#1D3557] font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field"
                      placeholder="your@email.com"
                      required
                      data-testid="signup-email-input"
                    />
                  </div>
                  <div className="flex items-start gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="arc-interest"
                      checked={interestedInArc}
                      onChange={(e) => setInterestedInArc(e.target.checked)}
                      className="w-5 h-5 mt-1"
                      data-testid="arc-interest-checkbox"
                    />
                    <label htmlFor="arc-interest" className="text-[#1D3557] font-medium">
                      I'm interested in receiving an ARC (Advanced Reader Copy) for review!
                    </label>
                  </div>
                  {signupStatus?.error && (
                    <p className="text-[#E63946] font-medium">{signupStatus.message}</p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full mt-4"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      "Signing up..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 inline mr-2" />
                        Join the Early Readers List
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Explore More */}
      <section className="py-16 px-4 md:px-8 bg-[#E63946]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            While You Wait...
          </h2>
          <p className="text-lg text-white/90 mb-8 font-medium">
            Check out our blog, livestock lessons, and fun activities!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Link
              to="/lessons"
              className="bg-[#2A9D8F] text-white border-4 border-[#1D3557] rounded-2xl shadow-[4px_4px_0px_0px_rgba(29,53,87,1)] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(29,53,87,1)] font-bold px-8 py-4 text-lg transition-all inline-flex items-center justify-center gap-2"
              data-testid="lessons-btn"
            >
              <BookOpen className="w-5 h-5" />
              Livestock Lessons
            </Link>
            <Link
              to="/blog"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="explore-blog-btn"
            >
              <BookOpen className="w-5 h-5" />
              Explore the Blog
            </Link>
            <Link
              to="/activities"
              className="bg-white text-[#1D3557] border-4 border-[#1D3557] rounded-2xl shadow-[4px_4px_0px_0px_rgba(29,53,87,1)] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(29,53,87,1)] font-bold px-8 py-4 text-lg transition-all inline-flex items-center justify-center gap-2"
              data-testid="activities-btn"
            >
              <Sparkles className="w-5 h-5" />
              Fun Activities
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
